/*----------------------------------
- CATALOG: PUBLIC DOMAINS TABLE COLUMNS
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @docs docs/features/plain-words-vocabulary
 * @adr ADR-0028
 * @rule Ids, order, labels and provenance all come from the shared catalog. The public listing and
 *       the product table must show the same evidence: a visitor comparing the two should never
 *       find a column that exists only on one of them.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import {
    COLUMN_PROVENANCE,
    PUBLIC_DOMAINS_TABLE_COLUMN_IDS,
    type ColumnProvenanceEntry,
} from '@uniquedomains/components/domains/catalogs/columnProvenance';

/*----------------------------------
- TYPES
----------------------------------*/

export type PublicDomainsTableColumn = {
    id: string;
    label: string;
    /** Track width in pixels, from the mockup. */
    width: number;
    align: 'left' | 'right';
    provenance: ColumnProvenanceEntry;
};

/*----------------------------------
- CATALOG
----------------------------------*/

/** Widths and labels, keyed by the shared column ids. */
const COLUMN_LAYOUT: Record<string, { label: string; width: number; align: 'left' | 'right' }> = {
    acquisition: { label: 'Status', width: 76, align: 'left' },
    fingerprint: { label: 'Word profile', width: 140, align: 'left' },
    timing: { label: 'Timing', width: 100, align: 'left' },
    signals: { label: 'Signals', width: 76, align: 'left' },
    verdict: { label: 'Rating', width: 72, align: 'left' },
    askPrice: { label: 'Price', width: 64, align: 'right' },
    upside: { label: 'Est. profit', width: 108, align: 'right' },
    similarSales: { label: 'Similar sales', width: 224, align: 'left' },
    comps: { label: 'TLD coverage', width: 120, align: 'right' },
    liquidity: { label: 'Liquidity', width: 80, align: 'right' },
    renewal: { label: 'Renews', width: 68, align: 'right' },
};

export const PUBLIC_DOMAINS_TABLE_COLUMNS: readonly PublicDomainsTableColumn[] =
    PUBLIC_DOMAINS_TABLE_COLUMN_IDS.flatMap((id) => {
        const layout = COLUMN_LAYOUT[id];
        const provenance = COLUMN_PROVENANCE[id];
        return layout && provenance ? [{ id, ...layout, provenance }] : [];
    });

/**
 * Horizontal padding on every table cell, half the 24px gutter on each side.
 *
 * Declared here because the `<colgroup>` has to add it back: widths in this catalog are content
 * widths, matching the tracks the product's grid lays out with `column-gap`.
 */
export const PUBLIC_TABLE_CELL_GUTTER = 12;

/**
 * Outer inset on the two pinned tracks, matching the 20px the product's grid gives its sticky
 * identity and actions cells. Their `<col>` widths add it back on the outer side and the half
 * gutter on the inner one.
 */
export const PUBLIC_TABLE_EDGE_INSET = 20;

/** Identity and actions tracks, which the table sets rather than the catalog. */
export const PUBLIC_TABLE_IDENTITY_WIDTH = 216;
export const PUBLIC_TABLE_ACTIONS_WIDTH = 40;

/**
 * The order label the public listing shows.
 *
 * The public order is the engine's daily shuffle, so "Relevance" is the only honest word for it.
 * The control beside it opens authentication rather than reordering (ADR-0028).
 */
export const PUBLIC_TABLE_SORT_LABEL = 'Relevance';
